import { Injectable, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface AuthCredentials {
  shopId: number;
  userId: number;
  token: string;
}

interface AuthSessionPayload extends AuthCredentials {
  exp: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private readonly SESSION_STORAGE_KEY = 'feather_subscription_session';
  private readonly SESSION_TTL_SECONDS = 30 * 60; // 30 minutes
  private readonly KEY_HEX = environment.authFragmentKey;

  // Store credentials in memory only (not localStorage for security)
  private _shopId = signal<number | null>(null);
  private _userId = signal<number | null>(null);
  private _token = signal<string | null>(null);
  private _initialized = signal<boolean>(false);
  private _error = signal<string | null>(null);

  // Public readonly signals
  readonly shopId = this._shopId.asReadonly();
  readonly userId = this._userId.asReadonly();
  readonly token = this._token.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed: check if all credentials are valid
  readonly isAuthenticated = computed(() => {
    return this._shopId() !== null &&
           this._userId() !== null &&
           this._token() !== null &&
           this._shopId()! > 0 &&
           this._userId()! > 0 &&
           this._token()!.length > 0;
  });

  // Computed: get credentials object
  readonly credentials = computed<AuthCredentials | null>(() => {
    if (!this.isAuthenticated()) return null;
    return {
      shopId: this._shopId()!,
      userId: this._userId()!,
      token: this._token()!
    };
  });

  constructor() {
    // Bootstrap auth in a deterministic order:
    // 1) Try to restore from an encrypted, session-scoped snapshot so a simple
    //    page refresh on /subscription keeps the user authenticated.
    // 2) Then, read credentials from the current URL so the auth interceptor
    //    has them on first request when coming from a fresh link with params.
    // 3) Finally, subscribe to query param changes for later updates.
    void this.bootstrapAuth();
  }

  private async bootstrapAuth(): Promise<void> {
    await this.initializeFromSessionSnapshot();
    this.initializeFromRouterSnapshot();
    this.initializeFromQueryParams();
  }

  /**
   * Read credentials from the current router snapshot synchronously.
   * This ensures the Bearer token is available before any HTTP request is made
   * when coming from a URL that includes auth query parameters.
   */
  private initializeFromRouterSnapshot(): void {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const params = route.snapshot.queryParams;
    this.applyParams(params);
  }

  /**
   * Initialize credentials from URL query parameters (async, for later updates).
   * Expected URL format: ?shopId=123&userId=456&token=eyJhbGc...
   */
  private initializeFromQueryParams(): void {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      this.applyParams(params);
    });
  }

  /**
   * Parse and apply query params; set credentials or error.
   */
  private applyParams(params: Record<string, string | undefined>): void {
    const shopIdStr = params['shopId'];
    const userIdStr = params['userId'];
    const token = params['token'];

    // If no auth-related query params are present at all:
    // - If we already have valid credentials (e.g. from storage or fragment),
    //   just ensure we are marked initialized and keep the existing auth.
    // - Otherwise, mark initialized with a descriptive error so the UI can
    //   show a clear message instead of a perpetual loading spinner.
    if (!shopIdStr && !userIdStr && !token) {
      if (this.isAuthenticated()) {
        this._initialized.set(true);
        return;
      } else {
        this._error.set('Missing authentication parameters in URL');
        this._initialized.set(true);
        return;
      }
    }

    // Validate and parse shopId
    const shopId = shopIdStr ? parseInt(shopIdStr, 10) : null;
    if (shopIdStr && (isNaN(shopId!) || shopId! <= 0)) {
      this._error.set('Invalid shopId parameter');
      this._initialized.set(true);
      return;
    }

    // Validate and parse userId
    const userId = userIdStr ? parseInt(userIdStr, 10) : null;
    if (userIdStr && (isNaN(userId!) || userId! <= 0)) {
      this._error.set('Invalid userId parameter');
      this._initialized.set(true);
      return;
    }

    // Check for missing required params
    if (!shopId || !userId || !token) {
      const missing: string[] = [];
      if (!shopId) missing.push('shopId');
      if (!userId) missing.push('userId');
      if (!token) missing.push('token');
      this._error.set(`Missing required parameters: ${missing.join(', ')}`);
      this._initialized.set(true);
      return;
    }

    // All params valid, set credentials
    this._shopId.set(shopId);
    this._userId.set(userId);
    this._token.set(token);
    this._error.set(null);
    this._initialized.set(true);

    console.log('Auth credentials initialized:', { shopId, userId, tokenLength: token.length });
  }

  /**
   * Manually set credentials (useful for testing or alternative auth flows)
   */
  setCredentials(shopId: number, userId: number, token: string): void {
    this._shopId.set(shopId);
    this._userId.set(userId);
    this._token.set(token);
    this._error.set(null);
    this._initialized.set(true);

    // Persist an encrypted, session-scoped snapshot so a page refresh keeps
    // the user authenticated without storing raw credentials in long-lived
    // browser storage.
    void this.persistSessionSnapshot({ shopId, userId, token });
  }

  /**
   * Clear all credentials (e.g., on logout or error)
   */
  clearCredentials(): void {
    this._shopId.set(null);
    this._userId.set(null);
    this._token.set(null);
    this._initialized.set(false);
    try {
      sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
    } catch {
      // Ignore storage failures (e.g. disabled storage), state is already cleared in memory.
    }
  }

  /**
   * Get the Authorization header value
   */
  getAuthorizationHeader(): string | null {
    const token = this._token();
    return token ? `Bearer ${token}` : null;
  }

  /**
   * Restore credentials from an encrypted, session-scoped snapshot if
   * available and valid.
   */
  private async initializeFromSessionSnapshot(): Promise<void> {
    let encoded: string | null = null;
    try {
      encoded = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
    } catch {
      // Storage may be unavailable (e.g. private mode); treat as no session.
      return;
    }

    if (!encoded) {
      return;
    }

    try {
      const bytes = this.base64UrlDecode(encoded);
      // Need at least 12 bytes of nonce + 16 bytes of tag
      if (bytes.length < 28) {
        sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
        return;
      }

      const iv = bytes.slice(0, 12);
      const ciphertextWithTag = bytes.slice(12);

      const key = await this.importKey();
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, tagLength: 128 },
        key,
        ciphertextWithTag
      );

      const json = new TextDecoder().decode(decrypted);
      const payload: AuthSessionPayload = JSON.parse(json);

      const nowSeconds = Math.floor(Date.now() / 1000);
      if (payload.exp < nowSeconds) {
        sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
        return;
      }

      this._shopId.set(payload.shopId);
      this._userId.set(payload.userId);
      this._token.set(payload.token);
      this._error.set(null);
      this._initialized.set(true);
    } catch {
      try {
        sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
      } catch {
        // Ignore storage failures; session is effectively unusable.
      }
    }
  }

  /**
   * Persist credentials as an encrypted, session-scoped snapshot.
   */
  private async persistSessionSnapshot(creds: AuthCredentials): Promise<void> {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const payload: AuthSessionPayload = {
      ...creds,
      exp: nowSeconds + this.SESSION_TTL_SECONDS
    };

    try {
      const key = await this.importKey();
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const data = new TextEncoder().encode(JSON.stringify(payload));

      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv, tagLength: 128 },
        key,
        data
      );

      const ciphertext = new Uint8Array(encrypted);
      const combined = new Uint8Array(iv.length + ciphertext.length);
      combined.set(iv, 0);
      combined.set(ciphertext, iv.length);

      const encoded = this.base64UrlEncode(combined);

      sessionStorage.setItem(this.SESSION_STORAGE_KEY, encoded);
    } catch {
      // If crypto or storage is unavailable, skip persistence; auth still
      // works for the current in-memory session.
    }
  }

  /**
   * Import AES-GCM key for encrypting/decrypting the session snapshot.
   */
  private async importKey(): Promise<CryptoKey> {
    const keyBytes = this.hexToBytes(this.KEY_HEX);
    const keyData = keyBytes.buffer as ArrayBuffer;
    return crypto.subtle.importKey('raw', keyData, 'AES-GCM', false, ['encrypt', 'decrypt']);
  }

  private base64UrlEncode(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    let base64 = btoa(binary);
    // Convert to URL-safe Base64
    base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    return base64;
  }

  private base64UrlDecode(str: string): Uint8Array {
    // Restore standard Base64 characters
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private hexToBytes(hex: string): Uint8Array {
    const len = hex.length;
    const bytes = new Uint8Array(len / 2);
    for (let i = 0; i < len; i += 2) {
      bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return bytes;
  }
}
